
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import type { SchoolTask } from '@/types/tasks'

export const useSchoolTasks = () => {
  const [schoolTasks, setSchoolTasks] = useState<SchoolTask[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchSchoolTasks = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        setLoading(false)
        return
      }

      // Get user's school_id from their profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('school_id')
        .eq('id', session.user.id)
        .single()

      let allTasks: SchoolTask[] = []

      // Only fetch school tasks for this user's school
      if (profile?.school_id) {
        const { data: schoolTasksData, error: schoolError } = await supabase
          .from('school_tasks')
          .select('*')
          .eq('school_id', profile.school_id)
          .order('priority', { ascending: false })
          .order('created_at', { ascending: false })

        if (schoolError) throw schoolError
        allTasks = [...(schoolTasksData || [])]
      }

      setSchoolTasks(allTasks)
    } catch (error: any) {
      console.error('Error fetching school tasks:', error)
      toast({
        title: "Error",
        description: "Failed to load school tasks",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSchoolTasks()

    // Set up real-time subscription for school tasks only
    const schoolChannel = supabase
      .channel('school-tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'school_tasks'
        },
        () => {
          fetchSchoolTasks()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(schoolChannel)
    }
  }, [])

  return {
    schoolTasks,
    loading,
    refreshSchoolTasks: fetchSchoolTasks
  }
}
